import { mm } from "@inovitas/infra3dsdk";
import { Close } from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";
import {
  DataGrid,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridColDef,
  GridRowSelectionModel,
} from "@mui/x-data-grid";
import { useEffect, useMemo, useRef, useState } from "react";

import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import { findMissingItems } from "shared/common/utils/general-utils";
import { lineIntersectsCircle } from "shared/common/utils/geometry/intersections";
import { VisibilityToggle } from "./VisibilityToggle";
import { useTranslation } from "react-i18next";

interface Props {
  handleClose?: () => void;
}

interface EnrichedAxisObject extends mm.AxisObject {
  id?: number;
  kmaxis?: string;
}

export default function AxisPanel({ handleClose }: Props) {
  const viewer = defaultStore((state) => state.viewer);
  const darkTheme = defaultStore((state) => state.darkTheme);
  const axisCollection = defaultStore((state) => state.axisCollection);
  const networkLayer = defaultStore((state) => state.networkLayer);
  const clientConfig = defaultStore((state) => state.clientConfig);

  const hoveredAxis = sidebarStore((state) => state.hoveredAxis);
  const changeHoveredAxis = sidebarStore((state) => state.changeHoveredAxis);
  const selectedAxis = sidebarStore((state) => state.selectedAxis);
  const changeSelectedAxis = sidebarStore((state) => state.changeSelectedAxis);
  const changeAxisPickTool = sidebarStore((state) => state.changeAxisPickTool);

  const { t } = useTranslation();

  // useRef to ensure the context in tool-cbf
  const enrichedAxisCollection = useRef<EnrichedAxisObject[]>([]);

  const [selectedRow, setSelectedRow] = useState<number>();
  const [hoveredRow, setHoveredRow] = useState<number>();

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 15,
  });
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [columns, setColumns] = useState<GridColDef<EnrichedAxisObject>[]>([]);

  useEffect(() => {
    const cols: GridColDef<EnrichedAxisObject>[] = [
      {
        field: "name",
        headerName: t("axis_panel.column_name") || "Name",
        flex: 1,
      },
      {
        ...GRID_CHECKBOX_SELECTION_COL_DEF,
        renderCell: (params) => (
          <VisibilityToggle
            checked={rowSelectionModel.includes(params.id)}
            onChange={(event: any) => {
              const axis = axisCollection.filter(
                (item) => item.axis?.name === params.row.name
              )[0].axis;

              networkLayer?.setAxisVisible(axis, event.target.checked);

              params.api.selectRow(params.id, event.target.checked);
            }}
          />
        ),
      },
    ];
    if (clientConfig?.linref?.type === "rail-ch") {
      cols.splice(1, 0, {
        field: "kmaxis",
        headerName: t("axis_panel.column_kmaxis") || "KM-Axis",
        flex: 0.5,
      });
    }
    setColumns(cols);
  }, [rowSelectionModel, axisCollection, networkLayer, t]);

  useEffect(() => {
    if (!hoveredAxis) {
      setHoveredRow(undefined);
      return;
    }

    const match = enrichedAxisCollection.current.find(
      (item) =>
        item.axis?.axis?.features[0].id === hoveredAxis.axis?.features[0].id
    );

    if (match) {
      setHoveredRow(match.id);
    }
  }, [hoveredAxis]);

  useEffect(() => {
    if (!selectedAxis) {
      setSelectedRow(undefined);
      return;
    }

    const match = enrichedAxisCollection.current.find(
      (item) => item.name === selectedAxis.name
    );

    if (match && viewer) {
      setSelectedRow(match.id);
      // set measurement axis to the Axis selected axis
      viewer.getScene().getLRHandler().setActiveMeasurementAxis(match.axis);

      const index = enrichedAxisCollection.current.findIndex(
        (item) => item.id === match.id
      );
      const page = Math.floor(index / paginationModel.pageSize);
      setPaginationModel((prev) => ({ ...prev, page }));
    }
  }, [selectedAxis, enrichedAxisCollection.current]);

  const highlightActiveNavigationAxis = () => {
    const selectedAxis = viewer
      ?.getScene()
      .getLRHandler()
      .getActiveNavigationAxis();
    if (!selectedAxis) return;

    const match = enrichedAxisCollection.current.find(
      (item) => item.name === selectedAxis.name
    );
    if (match) {
      setSelectedRow(match.id);
      changeSelectedAxis(match.axis);
    }
  };

  // Memoize axisCollection for axis in render distance
  const visibleAxisCollection = useMemo(() => {
    const geoframe = viewer?.getScene().getCurrentGeoframe();
    if (!geoframe) return [];
    return axisCollection
      .filter((row) => {
        if (clientConfig?.linref?.type === "rail-ch") {
          // Filter out axes that are not navigation references
          // to just show gleisachsen in rail-ch setups
          if (row.axis?.isnavigationref) return false;
        }

        if (!row.axis.axis?.features) return false;

        // Check if any segment of the axis intersects the geoframe radius
        const radius = clientConfig?.render_distance || 100;
        const center = { x: geoframe.easting, y: geoframe.northing };

        for (const feature of row.axis.axis?.features) {
          const coords = feature.geometry.coordinates;
          if (lineIntersectsCircle(coords, center, radius)) return true;
        }
        return false;
      })
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((item, index, arr) => {
        return {
          ...item,
          id: index,
          kmaxis: item.axis.axis?.features[0].properties.idkilometerachse || "",
        };
      });
  }, [axisCollection, viewer?.getScene().getCurrentGeoframe()]);

  useEffect(() => {
    setRowSelectionModel(visibleAxisCollection.map((row) => row.id)); // sets checked (visible) rows
    enrichedAxisCollection.current = visibleAxisCollection; // sets rows
  }, [visibleAxisCollection]);

  useEffect(() => {
    createAxisPickTool();
    return () => {
      removeAxisPickTool();
    };
  }, []);

  const handleAxisVisibility = (selectedRows: GridRowSelectionModel) => {
    if (!networkLayer) return;

    const result = findMissingItems(selectedRows, rowSelectionModel);
    const {
      itemsInArray2ButNotInArray1: addedAxis,
      ItemsInArray1ButNotInArray2: removedAxis,
    } = result;

    if (addedAxis.length > 0) {
      addedAxis.forEach((axisId) => {
        const axis = visibleAxisCollection[axisId]?.axis;
        networkLayer.setAxisVisible(axis, false);
      });
    }

    if (removedAxis.length > 0) {
      removedAxis.forEach((axisId) => {
        const axis = visibleAxisCollection[axisId]?.axis;
        networkLayer.setAxisVisible(axis, true);
      });
    }
  };

  const handleRowEnter = (event: React.MouseEvent<HTMLDivElement>) => {
    const rowId = event.currentTarget.getAttribute("data-id");
    const rowData = enrichedAxisCollection.current.find(
      (row) => row.id?.toString() === rowId
    );

    if (rowData) {
      // highlight all the features in the hovered axis
      networkLayer?.addAxisToSelection(rowData.axis);
    }
  };

  const handleRowLeave = (event: React.MouseEvent<HTMLDivElement>) => {
    // disable hightlight on the row the mouse just left
    const rowId = event.currentTarget.getAttribute("data-id");
    if (rowId === selectedRow?.toString()) return;
    const rowData = enrichedAxisCollection.current.find(
      (row) => row.id?.toString() === rowId
    );

    const match = enrichedAxisCollection.current.find(
      (item) =>
        item.axis.axis?.features[0].id === selectedAxis?.axis?.features[0].id
    );

    if (rowData && rowData?.id !== match?.id) {
      networkLayer?.removeAxisFromSelection(rowData.axis);
    }
  };

  const createAxisPickTool = () => {
    if (!viewer) return;

    const tooloptions = {
      selectedcbf: (selected_feature: mm.Feature) => {
        const matchedAxis = enrichedAxisCollection.current.find(
          (axis: mm.AxisObject) =>
            axis.axis?.axis?.features.some(
              (feature: mm.Feature) => feature.id === selected_feature.id
            )
        );
        if (!viewer || !matchedAxis) return;
        networkLayer?.featureselector.clearSelection();
        networkLayer?.addAxisToSelection(matchedAxis.axis);
        changeSelectedAxis(matchedAxis.axis);
        viewer
          .getScene()
          .getLRHandler()
          .setActiveMeasurementAxis(matchedAxis.axis);
      },
      deselectedcbf: (feature_deselected: mm.Feature) => {
        const matchedAxis = enrichedAxisCollection.current.find((axis) =>
          axis.axis?.axis?.features.some(
            (feature: mm.Feature) => feature.id === feature_deselected.id
          )
        );
        if (!matchedAxis) return;
        networkLayer?.removeAxisFromSelection(matchedAxis.axis);
        networkLayer?.axisHighlightOn(matchedAxis.axis); // keep the axis highlighted, as the cursor is still on it
        changeSelectedAxis(null);
        viewer.getScene().getLRHandler().setActiveMeasurementAxis(null);
      },
      clearcbf: () => {
        networkLayer?.featureselector.clearSelection();
        changeSelectedAxis(null);
        viewer.getScene().getLRHandler().setActiveMeasurementAxis(null);
      },
      onovercbf: (feature_hovered: mm.Feature) => {
        const axis = enrichedAxisCollection.current.find((axis) =>
          axis.axis?.axis?.features.some(
            (feature: mm.Feature) => feature.id === feature_hovered.id
          )
        );
        if (!axis) return;
        networkLayer?.axisHighlightOn(axis.axis);
        changeHoveredAxis(axis.axis);
      },
      onoutcbf: (feature_out: mm.Feature) => {
        changeHoveredAxis(null);
        const axis = enrichedAxisCollection.current.find((axis) =>
          axis.axis?.axis?.features.some(
            (feature: mm.Feature) => feature.id === feature_out.id
          )
        );
        if (!axis) return;
        if (axis.axis === sidebarStore.getState().selectedAxis) return; // do not remove selection if the axis is selected
        networkLayer?.axisHighlightOff(axis.axis);
      },
      hightlightonhover: false,
      activecursor: "pointer",
      clearonnullpick: true,
      selectionmodel: mm.SELECTIONMODEL.SIMPLE,
    };

    const _axisPickTool = viewer
      .getScene()
      .getToolHandler()
      .createSelectFeatureTool(tooloptions, "axisPick");

    viewer
      .getScene()
      .getLayerHandler()
      .layers.forEach((layer: mm.Layer) => {
        if (layer.type === mm.LAYERTYPE.NETWORKLAYER)
          _axisPickTool.addToPickLayer(layer.getFeatureCollection());
      });

    if (!sidebarStore.getState().selectTools?.enabled) _axisPickTool.enable();

    changeAxisPickTool(_axisPickTool);
  };

  function removeAxisPickTool() {
    const _axisPickTool = sidebarStore.getState().axisPickTool;
    if (!viewer || !_axisPickTool) return;

    _axisPickTool.disable();
    viewer.getScene().getToolHandler().removeTool(_axisPickTool);
    changeAxisPickTool(undefined);
  }

  return (
    <Box
      id="AxisVisibilityPanel"
      sx={{
        height: "calc(100% - 24px)",
        padding: 1.5,
        backgroundColor: darkTheme ? "darkblue.main" : "white",
        color: darkTheme ? "white" : "darkblue.main",
        overflow: "hidden",
        borderRadius: "10px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "18px",
          }}
        >
          {t("axis_panel.title")}
        </Typography>

        <IconButton disableRipple onClick={handleClose}>
          <Close
            sx={{
              color: darkTheme ? "white" : "darkblue.main",
              "@media (hover: hover)": {
                ":hover": { color: "secondary.main" },
              },
            }}
          />
        </IconButton>
      </Box>
      <Box sx={{ paddingBlockEnd: 1 }}>
        <Typography variant="body2">{t("axis_panel.description")}</Typography>
      </Box>
      <Box sx={{ width: "100%", flex: 1 }}>
        <DataGrid
          disableColumnMenu // remove this to get filtering etc.
          slots={{ baseCheckbox: VisibilityToggle }}
          onRowSelectionModelChange={(selectedRows) => {
            setRowSelectionModel(selectedRows);
            handleAxisVisibility(selectedRows);
          }}
          getRowClassName={(params) =>
            params.id === selectedRow || params.id === hoveredRow
              ? "Mui-force-hover"
              : ""
          }
          rowSelectionModel={rowSelectionModel}
          rows={enrichedAxisCollection.current}
          columns={columns}
          rowHeight={26}
          columnHeaderHeight={32}
          pagination
          checkboxSelection
          disableRowSelectionOnClick
          paginationModel={paginationModel}
          onPaginationModelChange={(model) => setPaginationModel(model)}
          pageSizeOptions={[15]}
          localeText={{
            footerRowSelected: (count) => {
              return `${count} ${count > 1 ? t("axis_panel.axes_visible") : t("axis_panel.axis_visible")}`;
            },
            MuiTablePagination: {
              labelDisplayedRows: ({ from, to, count }) =>
                `${from}–${to} ${t("axis_panel.of")} ${count}`,
            },
            noRowsLabel: t("general.loading") || "Loading...",
          }}
          slotProps={{
            row: {
              onMouseEnter: handleRowEnter,
              onMouseLeave: handleRowLeave,
            },
          }}
          onRowClick={(event) => {
            const axis = enrichedAxisCollection.current.find(
              (item) => item.id === event.row.id
            );
            if (axis) {
              networkLayer?.featureselector.clearSelection();

              // Remove selection if the row is already selected
              if (selectedRow === event.row.id) {
                setSelectedRow(undefined);
                changeSelectedAxis(null);
                return;
              }

              networkLayer?.addAxisToSelection(axis.axis);
              setSelectedRow(event.row.id);
              viewer
                ?.getScene()
                .getLRHandler()
                .setActiveMeasurementAxis(axis.axis);
              changeSelectedAxis(axis.axis);
            }
          }}
          sx={{
            height: "100%",
            // Global text color
            color: darkTheme ? "white" : "darkblue.main",
            // Remove outer table border
            "&, [class^=MuiDataGrid]": {
              border: "none",
            },
            // Row styling
            "& .MuiDataGrid-row": (theme) => ({
              cursor: "pointer",
              // userSelect: "none",
              backgroundColor: "transparent !important",
              "@media (hover: hover)": {
                "&:hover": {
                  color: theme.palette.secondary.main,
                },
              },
            }),
            "& .MuiDataGrid-row.Mui-force-hover": (theme) => ({
              color: theme.palette.secondary.main,
            }),
            // Cell and header focus outline removal
            "& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader": {
              "&:focus, &:focus-within": {
                outline: "none",
              },
            },
            // Pagination adjustments
            "& .MuiTablePagination-root": {
              color: darkTheme ? "white" : "darkblue.main",
            },
            "& .MuiTablePagination-toolbar": {
              marginLeft: "8px !important",
              paddingLeft: "0px !important",
            },
            "& .MuiTablePagination-actions": {
              marginLeft: "8px !important",
            },
          }}
        />
      </Box>
    </Box>
  );
}
